import { useEffect, useState } from 'react';
import { useAuthStore } from './store/useAuthStore';
import { useGameStore } from './store/useGameStore';
import { initTelegramWebApp, isInsideTelegram } from './lib/telegram';
import { getSocket } from './lib/socket';
import { fetchProfile } from './lib/api';
import { Lobby } from './screens/Lobby';
import { TableScreen } from './screens/Table';
import { ProfileScreen } from './screens/Profile';
import { LoadingState, ErrorState } from './components/StateViews';
import { LobbyTableSummary } from './shared/types';
import { AnimatePresence, motion } from 'framer-motion';

type Screen = 'lobby' | 'table' | 'profile';

export default function App() {
  const { status, login, error, token, profile, setProfile } = useAuthStore();
  const resetGame = useGameStore((s) => s.reset);

  const [screen, setScreen] = useState<Screen>('lobby');
  const [activeTable, setActiveTable] = useState<LobbyTableSummary | null>(null);

  useEffect(() => {
    initTelegramWebApp();
    login();
  }, []);

  // Единая точка подключения сокета + подписка на profile:update.
  // ВАЖНО: баланс фишек в UI ВСЕГДА обновляется только отсюда — по факту подтверждения
  // сервером, а не оптимистичным локальным расчётом. Раньше после выхода со стола фронт
  // держал устаревшее (заниженное на сумму бай-ина) значение — это чинит проблему.
  useEffect(() => {
    if (!token || status !== 'ready') return;
    const socket = getSocket(token);
    const onProfileUpdate = (p: any) => setProfile(p);
    socket.on('profile:update', onProfileUpdate);
    return () => {
      socket.off('profile:update', onProfileUpdate);
    };
  }, [token, status]);

  if (status === 'idle' || status === 'loading') {
    return <LoadingState label="Авторизация через Telegram…" />;
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <ErrorState
          message={
            isInsideTelegram()
              ? error ?? 'Не удалось авторизоваться'
              : 'Откройте приложение внутри Telegram, чтобы авторизоваться'
          }
          onRetry={login}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <AnimatePresence mode="wait">
        {screen === 'lobby' && (
          <motion.div key="lobby" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
            <Lobby
              onOpenTable={(table) => {
                setActiveTable(table);
                setScreen('table');
              }}
              onOpenProfile={() => setScreen('profile')}
            />
          </motion.div>
        )}

        {screen === 'table' && activeTable && (
          <motion.div key="table" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
            <TableScreen
              table={activeTable}
              onLeave={async () => {
                resetGame();
                setActiveTable(null);
                setScreen('lobby');
                // Доп. подстраховка поверх profile:update — на случай гонки/дисконнекта сокета
                // подтягиваем баланс напрямую через REST, чтобы лобби 100% показывало правду.
                if (token) {
                  try {
                    const fresh = await fetchProfile(token);
                    setProfile(fresh as any);
                  } catch {
                    /* profile:update от сокета обычно уже успевает применить актуальный баланс */
                  }
                }
              }}
            />
          </motion.div>
        )}

        {screen === 'profile' && (
          <motion.div key="profile" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
            <ProfileScreen onBack={() => setScreen('lobby')} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
